// Single currency system for the whole platform.
// The agency picks its currency once (Mon Agence → Devise); every amount
// renders through formatMoney with that code.

export const CURRENCIES = [
  { code: 'MAD', symbol: 'DH', label: 'Dirham (DH)' },
  { code: 'EUR', symbol: '€', label: 'Euro (€)' },
  { code: 'USD', symbol: '$', label: 'Dollar ($)' },
] as const;

export type CurrencyCode = 'MAD' | 'EUR' | 'USD';

/** Normalize legacy/dev values ('DH', lowercase…) to a known code. */
export function normalizeCurrency(code?: string | null): CurrencyCode {
  const c = (code ?? '').trim().toUpperCase();
  if (c === 'MAD' || c === 'DH' || c === 'درهم'.toUpperCase()) return 'MAD';
  if (c === 'EUR' || c === '€') return 'EUR';
  if (c === 'USD' || c === '$') return 'USD';
  return 'MAD';
}

export function currencySymbol(code?: string | null): string {
  const n = normalizeCurrency(code);
  return n === 'MAD' ? 'DH' : n === 'EUR' ? '€' : '$';
}

export function formatMoney(n: number, code?: string | null): string {
  const norm = normalizeCurrency(code);
  const locale = norm === 'MAD' ? 'fr-MA' : norm === 'EUR' ? 'fr-FR' : 'en-US';
  return `${Number(n || 0).toLocaleString(locale)} ${currencySymbol(norm)}`;
}

// Backward-compatible alias (website + legacy call sites).
export const CURRENCY = 'DH';

export function formatDH(n: number): string {
  return formatMoney(n, 'MAD');
}

export interface FxRates {
  rateEur: number;
  rateUsd: number;
}

/** Convert a MAD amount to EUR/USD equivalents for tourist display. */
export function convertEquivalents(
  mad: number,
  rates?: FxRates | null,
): { eur: number; usd: number } {
  const r = rates ?? { rateEur: 10.8, rateUsd: 10.0 };
  const eur = r.rateEur > 0 ? mad / r.rateEur : 0;
  const usd = r.rateUsd > 0 ? mad / r.rateUsd : 0;
  return { eur: Math.round(eur), usd: Math.round(usd) };
}

/** Convert an amount between currencies via MAD-based rates. */
export function convertAmount(
  n: number,
  from: string | null | undefined,
  to: string | null | undefined,
  rates?: FxRates | null,
): number {
  const f = normalizeCurrency(from);
  const t = normalizeCurrency(to);
  if (f === t) return n;
  const r = {
    rateEur: rates?.rateEur && rates.rateEur > 0 ? rates.rateEur : 10.8,
    rateUsd: rates?.rateUsd && rates.rateUsd > 0 ? rates.rateUsd : 10.0,
  };
  const toMad = f === 'MAD' ? n : f === 'EUR' ? n * r.rateEur : n * r.rateUsd;
  const out = t === 'MAD' ? toMad : t === 'EUR' ? toMad / r.rateEur : toMad / r.rateUsd;
  return Math.round(out);
}

/** Compact tourist hint, e.g. "≈ €23 · $25". Skips the given currency itself. */
export function formatEquivalents(
  mad: number,
  rates?: FxRates | null,
  agencyCode?: string | null,
): string {
  const { eur, usd } = convertEquivalents(mad, rates);
  const code = normalizeCurrency(agencyCode);
  const parts: string[] = [];
  if (code !== 'EUR') parts.push(`€${eur.toLocaleString('fr-FR')}`);
  if (code !== 'USD') parts.push(`$${usd.toLocaleString('en-US')}`);
  return parts.length ? `≈ ${parts.join(' · ')}` : '';
}
